import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiUsers, FiCalendar, FiHeart, FiActivity } from 'react-icons/fi';

const CounterSection = () => {
  const [counters, setCounters] = useState({
    dailyUsers: 0,
    monthlyUsers: 0,
    trackedCycles: 0,
    pregnanciesTracked: 0
  });

  const targetCounts = {
    dailyUsers: 2200,
    monthlyUsers: 66000,
    trackedCycles: 120000,
    pregnanciesTracked: 8520
  };

  useEffect(() => {
    const animateCounters = () => {
      Object.keys(targetCounts).forEach(key => {
        const target = targetCounts[key];
        const duration = 2000; // 2 seconds
        const startTime = Date.now();
        
        const animate = () => {
          const now = Date.now();
          const progress = Math.min(1, (now - startTime) / duration);
          
          setCounters(prev => ({
            ...prev,
            [key]: Math.floor(progress * target)
          }));
          
          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            setCounters(prev => ({
              ...prev,
              [key]: target
            }));
          }
        };
        
        requestAnimationFrame(animate);
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          animateCounters();
        }
      },
      { threshold: 0.1 }
    );

    const section = document.querySelector('#counter-section');
    if (section) observer.observe(section);

    return () => {
      if (section) observer.unobserve(section);
    };
  }, []);

  return (
    <section id="counter-section" className="pb-14 bg-gradient-to-br from-pink-50 to-purple-50 relative overflow-hidden">
      {/* Soft background circles */}
      <div className="absolute top-0 left-0 w-64 h-64 rounded-full bg-pink-200 opacity-10 blur-3xl -z-10"></div>
      <div className="absolute bottom-0 right-0 w-72 h-72 rounded-full bg-purple-200 opacity-10 blur-3xl -z-10"></div>
      
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-cormorant font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600">
            Our Community
          </h2>
          <p className="text-lg text-purple-700 mt-4 max-w-2xl mx-auto font-nunito">
          Join thousands of women supporting their health journey with us.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Daily Users */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 rounded-full bg-pink-100 text-pink-500">
                <FiUsers className="text-2xl" />
              </div>
              <h3 className="text-xl font-semibold text-purple-800">Daily Users</h3>
            </div>
            <p className="text-3xl text-center font-bold text-purple-900 font-nunito">
              {counters.dailyUsers.toLocaleString()}+
            </p>
          </motion.div>

          {/* Monthly Users */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 rounded-full bg-purple-100 text-purple-500">
                <FiCalendar className="text-2xl" />
              </div>
              <h3 className="text-xl font-nunito font-semibold text-purple-800">Monthly Users</h3>
            </div>
            <p className="text-3xl text-center font-bold text-purple-900 font-nunito">
              {counters.monthlyUsers.toLocaleString()}+
            </p>
          </motion.div>

          {/* Tracked Cycles */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 rounded-full bg-blue-100 text-blue-500">
                <FiActivity className="text-2xl" />
              </div>
              <h3 className="text-xl font-nunito font-semibold text-purple-800">Cycles Tracked</h3>
            </div>
            <p className="text-3xl text-center font-bold text-purple-900 font-nunito">
              {counters.trackedCycles.toLocaleString()}+
            </p>
          </motion.div>

          {/* Pregnancies Tracked */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 rounded-full bg-green-100 text-green-500">
                <FiHeart className="text-2xl" />
              </div>
              <h3 className="text-xl font-nunito font-semibold text-purple-800">Pregnancies Count</h3>
            </div>
            <p className="text-3xl text-center font-bold text-purple-900 font-nunito">
              {counters.pregnanciesTracked.toLocaleString()}+
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CounterSection;